import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild, AfterViewInit
} from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import { MatIconButton, MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatListModule } from '@angular/material/list';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmDialogComponent } from '../../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { MatInputModule } from '@angular/material/input';
import { Store } from '@ngxs/store';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { AppSelectors } from '../../../app.state';
import { TranslateComponent } from '../../../core/components/translation/translation.component';
import { ExerciseKind, Approval } from '../../../core/types';
// oben getExerciseKinds ggf
import { EditExerciseComponent } from '../edit-exercise/edit-exercise.component';
import { FreeFilterInputComponent } from '../../../utils/component/free-filter-input/free-filter-input.component';
import { getStatusText, StateFilterCBComponent } from '../../../utils/component/state-filter-combobox/state-cb.component';
import { TypeCBComponent } from '../../../utils/component/type-filter-combobox/type-cb.component';
import { getMinFilterLengthText } from '../../../core/util';
import { PlayerMessageService } from '../../../core/services/player.service';
import { PreviewApiService } from '../../../core/services/preview-api.service';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ExerciseEntity } from '../exercise.entity';
import { ExerciseEntityService } from '../services/exercise.entity.service';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { IFilters } from '@datx/jsonapi/interfaces/IFilters';
import { Observable, BehaviorSubject, combineLatest, switchMap, from, map, tap, firstValueFrom } from 'rxjs';
import { ConsumerEntity } from '../../../consumer/consumer.entity';
import { ConsumerEntityService } from '../../../consumer/services/consumer.entity.service';
import { WidgetMessageService } from '../../../core/services/widget-message.service';
import { EntityStateModel, InvalidateLoadedQueries } from '../../../core/state/entity.state';
import { SubjectEntityService } from '../../../subject/services/subject.entity.service';
import { SubjectEntity } from '../../../subject/subject.entity';
import { makeQueryKey } from '../../../utils/entity-states/query-key';
import { SimpleConfirmData } from '../../../core/components/confirm-dialog-raw/confirm-dialog-raw.component';
import { TableLoadingSpinnerComponent } from '../../../utils/component/table-loading-spinner/table-loading-spinner';
import { LocalStorageService } from '../../../core/services/local-storage.service';
import { ContentEntityComponent, ContentEntityDialogData } from '../../../core/components/content-entity/content-entity.component';

@Component({
  selector: 'pixl-exercise',
  imports: [
    MatIcon,
    MatSortModule,
    MatIconButton,
    MatTableModule,
    MatButtonModule,
    MatSelectModule,
    MatTooltipModule,
    MatDialogModule,
    MatListModule,
    MatInputModule,
    DatePipe,
    NgClass,
    TranslateComponent,
    TypeCBComponent,
    FreeFilterInputComponent,
    StateFilterCBComponent,
    TranslocoModule,
    MatPaginator,
    AsyncPipe,
    TableLoadingSpinnerComponent,
  ],
  templateUrl: './exercise.component.html',
  styleUrl: './exercise.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class ExerciseComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('editStateDiv') editStateRef!: ElementRef;

  pageSize = 20;
  pageIndex = 0;
  totalItems = 1000;

  private readonly store = inject(Store);
  public exerciseService = inject(ExerciseEntityService);
  private consumerService = inject(ConsumerEntityService);
  private subjectService = inject(SubjectEntityService);
  private widgetService = inject(WidgetMessageService);
  private previewApiService = inject(PreviewApiService);
  exercises$!: Observable<ExerciseEntity[]>;
  private dialog = inject(MatDialog);
  deletedIds$ = new BehaviorSubject<string[]>([]);
  private refresh$ = new BehaviorSubject<void>(undefined);
  private pageChange$ = new BehaviorSubject<{ pageIndex: number, pageSize: number }>({ pageIndex: 0, pageSize: this.pageSize });

  consumers$!: Observable<ConsumerEntity[]>;
  subjects$!: Observable<SubjectEntity[]>;

  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private playerService = inject(PlayerMessageService);

  public filters: IFilters = {};
  private sortString = 'name';
  private freeFilter = '';
  private consumerFilter: string[] = [];
  private subjectFilter: string[] = [];
  private statusFilter = '';
  private exerciseKindFilter = '';
  public readonly freeFilterLen = 3;

  private _liveAnnouncer = inject(LiveAnnouncer);
  private appState$ = this.store.select(AppSelectors.appState);
  private LIST_FIELDS: string[] | null = ['name', 'kind', 'approval', 'consumerId', 'subjectId', 'consumerData', 'config', 'consumer'];
  private translocoService = inject(TranslocoService);
  private localStorageService = inject(LocalStorageService);

  public dataSource = new MatTableDataSource<ExerciseEntity>([] as ExerciseEntity[]);
  public displayedColumns: string[] = ['edit-actions', 'id', 'kind', 'name', 'consumer', 'competency', 'updated', 'approval'];
  public kinds: ExerciseKind[] = [];
  public searchFilterHint = signal(false);
  public currentSelectedRow = signal<string | undefined>(undefined);
  public readonly coloredButtonsEnabled = this.localStorageService.coloredButtonsEnabled;

  ngOnInit() {
    this.loadRelationData();

    this.exercises$ = combineLatest([
      this.pageChange$,
      this.refresh$,
      this.deletedIds$
    ]).pipe(
      switchMap(([{ pageIndex, pageSize }, , deletedIds]) => {
        const query: Record<string, string | number> = {
          'include': 'consumers,subjects',
          'page[size]': pageSize,
          'page[number]': pageIndex + 1,
          'sort': this.sortString,
        };

        if (this.freeFilter?.trim()?.length >= 3) {
          query['page[number]'] = 0;
          query['filter[nameLike]'] = this.freeFilter.trim();
        }

        if (this.statusFilter?.length > 0) {
          query['page[number]'] = 0;
          query['filter[approval]'] = this.statusFilter.trim();
        }

        if (this.consumerFilter?.length > 0) {
          query['page[number]'] = 0;
          query['filter[consumers][id]'] = this.consumerFilter.join(',');
        }

        if (this.subjectFilter?.length > 0) {
          query['page[number]'] = 0;
          query['filter[subjectId]'] = this.subjectFilter.join(',');
        }

        if (this.exerciseKindFilter?.length > 0) {
          query['page[number]'] = 0;
          query['filter[kind]'] = this.exerciseKindFilter.trim();
        }

        return from(this.exerciseService.ensureLoaded(query)).pipe(
          switchMap(() => this.store.select(this.exerciseService.allByQuery(query))),
          map((examSequences: ExerciseEntity[]) => examSequences.filter(m => !deletedIds.includes(m.id))),
          tap(() => {
            const state = this.store.selectSnapshot(this.exerciseService.stateClass) as EntityStateModel<ExerciseEntity>;
            const queryKey = makeQueryKey(query);
            this.totalItems = state.meta?.[queryKey]?.page?.total ?? 0;
          })
        );
      })
    );
  }

  ngAfterViewInit(): void {
    this.paginator.page.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: PageEvent) => {
      this.pageChange$.next({ pageIndex: event.pageIndex, pageSize: event.pageSize });
    });
  }

  private loadRelationData(): void {
    const consumerQuery = { sort: 'name' };
    this.consumers$ = from(
      this.consumerService.ensureLoaded(consumerQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.consumerService.allByQuery(consumerQuery))
      )
    );

    const subjectQuery = { sort: 'name' };
    this.subjects$ = from(
      this.subjectService.ensureLoaded(subjectQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.subjectService.allByQuery(subjectQuery))
      )
    );
  }

  addExercise(combinedKind: string): void {
    const [kind, subKind] = combinedKind.split('.');
    const dialogRef = this.dialog.open(EditExerciseComponent, {
      width: '500px',
      data: {
        kind: kind,
        copyRestricted: false,
        name: '',
        config: subKind ? { exerciseSubKind: subKind } : undefined,
      }
    });

    dialogRef.afterClosed().subscribe(async (result: Partial<ExerciseEntity> | undefined) => {
      if (result) {
        const newEntityInstance = Object.assign(new ExerciseEntity(), result);
        await this.exerciseService.create(newEntityInstance);
        this.store.dispatch(new InvalidateLoadedQueries());
        this.refresh$.next();

        this.widgetService.setMessage({
          message: this.translocoService.translate('task_created'),
          messageTye: 'mtSuccess'
        });
      }
    });
  }

  public async copyExercise(examSequence: ExerciseEntity) {
    const copyEntityInstance = Object.assign(new ExerciseEntity(), examSequence);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (copyEntityInstance as any).id = undefined;
    copyEntityInstance.name = `${copyEntityInstance.name} (Kopie)`;
    copyEntityInstance.version = 1;
    await this.exerciseService.create(copyEntityInstance);
    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.widgetService.setMessage({
      message: this.translocoService.translate('task_cloned'),
      messageTye: 'mtSuccess'
    });
  }

  public async deleteExercise(exercise: ExerciseEntity): Promise<void> {
    if (!exercise?.id) {
      console.error('ID is undefined!', exercise);
      return;
    }

    const confirmed = await firstValueFrom(
      this.dialog
        .open<ConfirmDialogComponent, SimpleConfirmData, boolean>(ConfirmDialogComponent, {
          data: {
            name: String(exercise.name),
            headerText: this.translocoService.translate('task_delete'),
          }
        })
        .afterClosed()
    );

    if (!confirmed) return;

    await firstValueFrom(this.exerciseService.delete(exercise.id));

    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.widgetService.setMessage({
      message: this.translocoService.translate('task_deleted'),
      messageTye: 'mtSuccess'
    });
  }

  editExercise(exercise: ExerciseEntity): void {
    const dialogRef = this.dialog.open(EditExerciseComponent, {
      width: '800px',
      data: {
        ...exercise,
        consumerExtra: this.getConsumer(exercise.consumerId)
      },
    });

    dialogRef.afterClosed().subscribe(async (updatedExercise: ExerciseEntity | undefined) => {
      if (!updatedExercise) return;
      await firstValueFrom(this.exerciseService.update(updatedExercise));
      this.refresh$.next();

      this.widgetService.setMessage({
        message: this.translocoService.translate('task_saved'),
        messageTye: 'mtSuccess'
      });
    });
  }

  public editContentExercise(exercise: ExerciseEntity) {
    this.currentSelectedRow.set(exercise.id);
    const dialogRef = this.dialog.open(ContentEntityComponent, {
      minWidth: '90vw',
      height: '90vh',
      data: {
        type: 'exercise',
        model: exercise
      } as ContentEntityDialogData
    });

    dialogRef.afterClosed().subscribe(async (updatedExercise: ExerciseEntity | undefined) => {
      if (!updatedExercise) return;
      await firstValueFrom(this.exerciseService.update(updatedExercise));
      this.refresh$.next();

      this.widgetService.setMessage({
        message: this.translocoService.translate('task_saved'),
        messageTye: 'mtSuccess'
      });
    });
  }

  public getSearchFilterHint(): string {
    return getMinFilterLengthText(this.freeFilterLen);
  }

  public sortEntries(strA: string, strB: string) {
    return strA.toLocaleLowerCase().localeCompare(strB.toLowerCase());
  }

  public announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    }
    else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
    this.sortData(sortState);
  }

  public sortData(sort: Sort): void {
    this.sortString = sort.active;
    if (sort.direction == 'desc') {
      this.sortString = '-' + sort.active;
    }
    this.refresh$.next();
  }

  public getCreatedDate(id: string): Date {
    return new Date(parseInt(id.substring(0, 8), 16) * 1000);
  }

  public getStateText(approval: Approval): string {
    return this.translocoService.translate(getStatusText(approval));
  }

  public getStatusColor(approval: Approval): string {
    return {
      Dormant: 'badge-dormant',
      Edit: 'badge-edit',
      Pending: 'badge-pending',
      Live: 'badge-live',
    }[approval] || '';
  }

  getConsumer(exerciseConsumerId: string, getName = false): ConsumerEntity | string {
    if (!exerciseConsumerId) return '---';

    const state = this.store.selectSnapshot(this.consumerService.stateClass);
    const consumer: ConsumerEntity = this.consumerService.stateClass.byId(state)(exerciseConsumerId);

    if (getName) {
      return consumer?.name || exerciseConsumerId;
    }
    return consumer;
  }

  getSubject(exerciseSubjectId: string, getName = false): ExerciseEntity | string {
    if (!exerciseSubjectId) return '---';

    const state = this.store.selectSnapshot(this.subjectService.stateClass);
    const subject: ExerciseEntity = this.subjectService.stateClass.byId(state)(exerciseSubjectId);

    if (getName) {
      return subject?.name || exerciseSubjectId;
    }
    return subject;
  }

  public setFreeFilter(input: string): void {
    this.currentSelectedRow.set(undefined); // unmark the select row
    this.freeFilter = input;
    this.refresh$.next();
  }

  public setConsumerFilter(selected: string[]): void {
    this.currentSelectedRow.set(undefined); // unmark the select row
    this.consumerFilter = selected;
    this.refresh$.next();
  }

  public setSubjectFilter(selected: string[]): void {
    this.currentSelectedRow.set(undefined); // unmark the select row
    this.subjectFilter = selected;
    this.refresh$.next();
  }

  public setStatusFilter(selected: string): void {
    this.currentSelectedRow.set(undefined); // unmark the select row
    this.statusFilter = selected;
    this.refresh$.next();
  }

  public setKindFilter(selected: string): void {
    this.currentSelectedRow.set(undefined); // unmark the select row
    this.exerciseKindFilter = selected;
    this.refresh$.next();
  }

  public changeExerciseState(exercise: ExerciseEntity) {
    if (exercise.approval === 'Dormant') {
      return;
    }

    if (this.editStateRef.nativeElement.classList.contains('open')) {
      this.editStateRef.nativeElement.classList.remove('open');
    }
    else {
      this.editStateRef.nativeElement.classList.add('open');
    }
  }

  public previewPlayer2(exercise: ExerciseEntity) {
    this.currentSelectedRow.set(exercise.id);
    this.previewApiService.upsertPreview('exercise', exercise.id, exercise).subscribe({
      next: () => this.playerService.openPlayerPreview(exercise.consumerId, 'exercise', exercise.id),
      error: () => this.widgetService.setMessage({
        message: this.translocoService.translate('preview.saveError'),
        messageTye: 'mtError'
      })
    });
  }
}
